import { useLazyQuery, useMutation } from '@apollo/client'
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material'
import {
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { recruiterRouteConfig } from 'recruiter/routes'
import CompaniesAutocomplete from 'shared/components/CompaniesAutocomplete/CompaniesAutocomplete'
import useToast from 'shared/components/Toast/useToast'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { getPxAsRem } from 'shared/utils'
import {
    type GetCompanyMergePreviewQuery,
    type MergeCompaniesMutation,
} from '__generated__/graphql'
import { GetCompanyMergePreview, MergeCompanies } from './CompanyMerge.gql'

interface CompanyOption {
    id: string
    value: string
}

type CompanyData = GetCompanyMergePreviewQuery['company']

type MergeResultData = NonNullable<MergeCompaniesMutation['mergeCompanies']>

interface CompanyStats {
    name: string
    notesCount: number
    documentsCount: number
    officesCount: number
    industriesCount: number
    sectorsCount: number
    fundsCount: number
    fundSizeBucketsCount: number
    recruiterContactsCount: number
    workExperienceCount: number
    rostersCount: number
    jobBoardsCount: number
    clientsCount: number
    candidateReferencesCount: number
    candidateNotesCount: number
    groupNotesCount: number
}

type StatsKey = Exclude<keyof CompanyStats, 'name'>

const STAT_ROWS: { label: string; key: StatsKey; expandable?: boolean }[] = [
    { label: 'Notes', key: 'notesCount', expandable: true },
    { label: 'Documents', key: 'documentsCount', expandable: true },
    { label: 'Offices', key: 'officesCount', expandable: true },
    { label: 'Industries', key: 'industriesCount', expandable: true },
    { label: 'Sectors', key: 'sectorsCount', expandable: true },
    { label: 'Funds', key: 'fundsCount', expandable: true },
    {
        label: 'Fund Size Buckets',
        key: 'fundSizeBucketsCount',
        expandable: true,
    },
    {
        label: 'Recruiter Contacts',
        key: 'recruiterContactsCount',
        expandable: true,
    },
    { label: 'Work Experience', key: 'workExperienceCount', expandable: true },
    { label: 'Rosters', key: 'rostersCount', expandable: true },
    { label: 'Job Boards', key: 'jobBoardsCount', expandable: true },
    { label: 'Clients', key: 'clientsCount', expandable: true },
    {
        label: 'Candidate References',
        key: 'candidateReferencesCount',
        expandable: true,
    },
    { label: 'Candidate Notes', key: 'candidateNotesCount', expandable: true },
    { label: 'Group Notes', key: 'groupNotesCount', expandable: true },
]

function toStats(data: CompanyData | MergeResultData): CompanyStats {
    const name = 'value' in data ? data.value : data.name
    const officesCount =
        ('offices' in data ? data.offices : data.companyOffices)?.length ?? 0

    return {
        name,
        notesCount: data.notes?.length ?? 0,
        documentsCount: data.documents?.length ?? 0,
        officesCount,
        industriesCount: data.industries?.length ?? 0,
        sectorsCount: data.sectors?.length ?? 0,
        fundsCount: data.companyFunds?.length ?? 0,
        fundSizeBucketsCount: data.fundSizeBuckets?.length ?? 0,
        recruiterContactsCount: data.recruiterContacts?.length ?? 0,
        workExperienceCount: data.workExperience?.length ?? 0,
        rostersCount: data.rosters?.length ?? 0,
        jobBoardsCount: data.jobBoards?.length ?? 0,
        clientsCount: data.clients?.length ?? 0,
        candidateReferencesCount: data.candidateReferences?.length ?? 0,
        candidateNotesCount: data.candidateNotes?.length ?? 0,
        groupNotesCount: data.groupNotes?.length ?? 0,
    }
}

export default function CompanyMergePage() {
    usePageTitle('Merge Companies')
    const toast = useToast()
    const navigate = useNavigate()

    const [leftCompany, setLeftCompany] = useState<CompanyOption | null>(null)
    const [rightCompany, setRightCompany] = useState<CompanyOption | null>(null)
    const [confirmOpen, setConfirmOpen] = useState(false)

    const [fetchLeft, { data: leftData }] = useLazyQuery(
        GetCompanyMergePreview,
        { fetchPolicy: 'network-only' }
    )
    const [fetchRight, { data: rightData }] = useLazyQuery(
        GetCompanyMergePreview,
        { fetchPolicy: 'network-only' }
    )

    const [mergePreview, { data: previewData, loading: previewing }] =
        useMutation(MergeCompanies, { fetchPolicy: 'no-cache' })

    const [mergeCompanies, { loading: merging, client }] = useMutation(
        MergeCompanies,
        {
            onCompleted: () => {
                client.cache.evict({ fieldName: 'company' })
                client.cache.evict({ fieldName: 'companies' })
                client.cache.gc()
                toast({
                    title: 'Success',
                    severity: 'success',
                    message: 'Companies merged successfully',
                })
                navigate(
                    `${recruiterRouteConfig.companies.path}/${rightCompany!.id}`
                )
            },
            onError: (error) => {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: error.message,
                })
            },
        }
    )

    const handleLeftSelect = (_: unknown, value: CompanyOption | null) => {
        setLeftCompany(value)
        if (value) fetchLeft({ variables: { companyId: value.id } })
    }

    const handleRightSelect = (_: unknown, value: CompanyOption | null) => {
        setRightCompany(value)
        if (value) fetchRight({ variables: { companyId: value.id } })
    }

    useEffect(() => {
        if (leftCompany && rightCompany && leftCompany.id !== rightCompany.id) {
            mergePreview({
                variables: {
                    winnerId: rightCompany.id,
                    loserId: leftCompany.id,
                    liveRun: false,
                },
            })
        }
    }, [leftCompany, rightCompany])

    const handleMerge = () => {
        setConfirmOpen(false)
        mergeCompanies({
            variables: {
                winnerId: rightCompany!.id,
                loserId: leftCompany!.id,
                liveRun: true,
            },
        })
    }

    const leftStats = leftData?.company ? toStats(leftData.company) : undefined
    const rightStats = rightData?.company
        ? toStats(rightData.company)
        : undefined
    const preview = previewData?.mergeCompanies
        ? toStats(previewData.mergeCompanies)
        : undefined

    return (
        <Stack
            direction="column"
            spacing={2}
            sx={{
                padding: getPxAsRem(16, 24),
                height: '100%',
                overflow: 'auto',
            }}
        >
            <Typography variant="h2">Merge Companies</Typography>
            <Stack direction="row" spacing={2}>
                <Card sx={{ flex: 1, p: getPxAsRem(16) }}>
                    <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                    >
                        Company A (will be archived)
                    </Typography>
                    <CompaniesAutocomplete
                        value={leftCompany}
                        onChange={handleLeftSelect}
                        getOptionDisabled={(option: CompanyOption) =>
                            option.id === rightCompany?.id
                        }
                        label="Search company"
                        size="small"
                    />
                </Card>
                <Card sx={{ flex: 1, p: getPxAsRem(16) }}>
                    <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                    >
                        Company B (survivor)
                    </Typography>
                    <CompaniesAutocomplete
                        value={rightCompany}
                        onChange={handleRightSelect}
                        getOptionDisabled={(option: CompanyOption) =>
                            option.id === leftCompany?.id
                        }
                        label="Search company"
                        size="small"
                    />
                </Card>
            </Stack>
            <ComparisonTable
                leftStats={leftStats}
                rightStats={rightStats}
                leftCompany={leftData?.company}
                rightCompany={rightData?.company}
                preview={preview}
                mergeResult={previewData?.mergeCompanies}
                previewing={previewing}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={
                        !leftCompany ||
                        !rightCompany ||
                        leftCompany.id === rightCompany.id ||
                        merging
                    }
                    onClick={() => setConfirmOpen(true)}
                >
                    {leftCompany && rightCompany
                        ? `Merge "${leftCompany.value}" into "${rightCompany.value}"`
                        : 'Select two companies to merge'}
                </Button>
            </Box>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm Merge</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will transfer all associations from{' '}
                        <strong>{leftCompany?.value}</strong> into{' '}
                        <strong>{rightCompany?.value}</strong> and archive{' '}
                        {leftCompany?.value}. This action cannot be easily
                        undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleMerge}
                        variant="contained"
                        color="error"
                    >
                        Merge
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}

function ComparisonTable({
    leftStats,
    rightStats,
    leftCompany,
    rightCompany,
    preview,
    mergeResult,
    previewing,
}: {
    leftStats?: CompanyStats
    rightStats?: CompanyStats
    leftCompany?: CompanyData
    rightCompany?: CompanyData
    preview?: CompanyStats
    mergeResult?: MergeResultData
    previewing: boolean
}) {
    const hasData = leftStats || rightStats
    const [expandedRows, setExpandedRows] = useState<Set<StatsKey>>(new Set())

    const toggleRow = (key: StatsKey) => {
        setExpandedRows((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    return (
        <TableContainer
            component={Card}
            sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}
        >
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: '25%' }}>
                            Association
                        </TableCell>
                        <TableCell
                            align="right"
                            sx={{ fontWeight: 600, width: '25%' }}
                        >
                            {leftStats?.name ?? 'Company A'}
                        </TableCell>
                        <TableCell
                            align="right"
                            sx={{ fontWeight: 600, width: '25%' }}
                        >
                            {rightStats?.name ?? 'Company B'}
                        </TableCell>
                        <TableCell
                            align="right"
                            sx={(theme) => ({
                                fontWeight: 600,
                                width: '25%',
                                bgcolor:
                                    theme.palette.mode === 'dark'
                                        ? 'grey.800'
                                        : 'grey.100',
                            })}
                        >
                            Merge Result
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {!hasData ? (
                        <TableRow>
                            <TableCell
                                colSpan={4}
                                align="center"
                                sx={{ py: 4, color: 'text.secondary' }}
                            >
                                Select two companies to compare
                            </TableCell>
                        </TableRow>
                    ) : (
                        STAT_ROWS.map(({ label, key, expandable }) => {
                            const leftVal = leftStats?.[key] ?? 0
                            const rightVal = rightStats?.[key] ?? 0
                            const resultVal = preview?.[key]
                            const allZero =
                                leftVal === 0 &&
                                rightVal === 0 &&
                                (resultVal ?? 0) === 0
                            const isExpanded = expandedRows.has(key)
                            const canExpand = expandable && !allZero

                            return (
                                <StatRow
                                    key={key}
                                    statKey={key}
                                    label={label}
                                    leftVal={leftVal}
                                    rightVal={rightVal}
                                    resultVal={resultVal}
                                    allZero={allZero}
                                    hasLeft={!!leftStats}
                                    hasRight={!!rightStats}
                                    previewing={previewing}
                                    hasPreview={!!preview}
                                    canExpand={!!canExpand}
                                    isExpanded={isExpanded}
                                    onToggle={() => toggleRow(key)}
                                    leftCompany={leftCompany}
                                    rightCompany={rightCompany}
                                    mergeResult={mergeResult}
                                />
                            )
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

function StatRow({
    statKey,
    label,
    leftVal,
    rightVal,
    resultVal,
    allZero,
    hasLeft,
    hasRight,
    previewing,
    hasPreview,
    canExpand,
    isExpanded,
    onToggle,
    leftCompany,
    rightCompany,
    mergeResult,
}: {
    statKey: StatsKey
    label: string
    leftVal: number
    rightVal: number
    resultVal?: number
    allZero: boolean
    hasLeft: boolean
    hasRight: boolean
    previewing: boolean
    hasPreview: boolean
    canExpand: boolean
    isExpanded: boolean
    onToggle: () => void
    leftCompany?: CompanyData
    rightCompany?: CompanyData
    mergeResult?: MergeResultData
}) {
    return (
        <>
            <TableRow
                sx={{
                    ...(allZero ? { opacity: 0.4 } : undefined),
                    ...(canExpand
                        ? {
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' },
                          }
                        : undefined),
                }}
                onClick={canExpand ? onToggle : undefined}
            >
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {canExpand && (
                            <IconButton size="small" sx={{ mr: 0.5, p: 0 }}>
                                {isExpanded ? (
                                    <KeyboardArrowDown fontSize="small" />
                                ) : (
                                    <KeyboardArrowRight fontSize="small" />
                                )}
                            </IconButton>
                        )}
                        <Typography variant="body2">{label}</Typography>
                    </Box>
                </TableCell>
                <TableCell align="right">
                    <Typography variant="body2">
                        {hasLeft ? leftVal : '—'}
                    </Typography>
                </TableCell>
                <TableCell align="right">
                    <Typography variant="body2">
                        {hasRight ? rightVal : '—'}
                    </Typography>
                </TableCell>
                <TableCell align="right" sx={{ bgcolor: 'action.hover' }}>
                    <ResultCell
                        resultVal={resultVal}
                        winnerVal={rightVal}
                        previewing={previewing}
                        hasPreview={hasPreview}
                    />
                </TableCell>
            </TableRow>
            {canExpand && isExpanded && (
                <ExpandedRow
                    statKey={statKey}
                    leftCompany={leftCompany}
                    rightCompany={rightCompany}
                    mergeResult={mergeResult}
                />
            )}
        </>
    )
}

function ExpandedRow({
    statKey,
    leftCompany,
    rightCompany,
    mergeResult,
}: {
    statKey: StatsKey
    leftCompany?: CompanyData
    rightCompany?: CompanyData
    mergeResult?: MergeResultData
}) {
    const sortByDate = <T extends { createdAt: string }>(a: T, b: T) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()

    if (statKey === 'notesCount') {
        const leftNotes = [...(leftCompany?.notes ?? [])].sort(sortByDate)
        const rightNotes = [...(rightCompany?.notes ?? [])].sort(sortByDate)
        const resultNotes = [...(mergeResult?.notes ?? [])].sort(sortByDate)

        return (
            <ExpandedDetailRow
                left={<NotesList notes={leftNotes} />}
                right={<NotesList notes={rightNotes} />}
                result={<NotesList notes={resultNotes} />}
            />
        )
    }

    if (statKey === 'documentsCount') {
        const leftDocs = [...(leftCompany?.documents ?? [])].sort(sortByDate)
        const rightDocs = [...(rightCompany?.documents ?? [])].sort(sortByDate)
        const resultDocs = [...(mergeResult?.documents ?? [])].sort(sortByDate)

        return (
            <ExpandedDetailRow
                left={<DocumentsList documents={leftDocs} />}
                right={<DocumentsList documents={rightDocs} />}
                result={<DocumentsList documents={resultDocs} />}
            />
        )
    }

    if (statKey === 'officesCount') {
        const leftOffices = leftCompany?.companyOffices ?? []
        const rightOffices = rightCompany?.companyOffices ?? []
        const resultOffices = mergeResult?.offices ?? []

        return (
            <ExpandedDetailRow
                left={<OfficesList offices={leftOffices} />}
                right={<OfficesList offices={rightOffices} />}
                result={<OfficesList offices={resultOffices} />}
            />
        )
    }

    if (statKey === 'industriesCount') {
        const leftIndustries = leftCompany?.industries ?? []
        const rightIndustries = rightCompany?.industries ?? []
        const resultIndustries = mergeResult?.industries ?? []

        return (
            <ExpandedDetailRow
                left={<IndustriesList items={leftIndustries} />}
                right={<IndustriesList items={rightIndustries} />}
                result={<IndustriesList items={resultIndustries} />}
            />
        )
    }

    if (statKey === 'sectorsCount') {
        const leftSectors = leftCompany?.sectors ?? []
        const rightSectors = rightCompany?.sectors ?? []
        const resultSectors = mergeResult?.sectors ?? []

        return (
            <ExpandedDetailRow
                left={<SectorsList items={leftSectors} />}
                right={<SectorsList items={rightSectors} />}
                result={<SectorsList items={resultSectors} />}
            />
        )
    }

    if (statKey === 'fundsCount') {
        const leftFunds = leftCompany?.companyFunds ?? []
        const rightFunds = rightCompany?.companyFunds ?? []
        const resultFunds = mergeResult?.companyFunds ?? []

        return (
            <ExpandedDetailRow
                left={<FundsList items={leftFunds} />}
                right={<FundsList items={rightFunds} />}
                result={<FundsList items={resultFunds} />}
            />
        )
    }

    if (statKey === 'fundSizeBucketsCount') {
        const leftBuckets = leftCompany?.fundSizeBuckets ?? []
        const rightBuckets = rightCompany?.fundSizeBuckets ?? []
        const resultBuckets = mergeResult?.fundSizeBuckets ?? []

        return (
            <ExpandedDetailRow
                left={<FundSizeBucketsList items={leftBuckets} />}
                right={<FundSizeBucketsList items={rightBuckets} />}
                result={<FundSizeBucketsList items={resultBuckets} />}
            />
        )
    }

    if (statKey === 'recruiterContactsCount') {
        const leftContacts = leftCompany?.recruiterContacts ?? []
        const rightContacts = rightCompany?.recruiterContacts ?? []
        const resultContacts = mergeResult?.recruiterContacts ?? []

        return (
            <ExpandedDetailRow
                left={<RecruiterContactsList items={leftContacts} />}
                right={<RecruiterContactsList items={rightContacts} />}
                result={<RecruiterContactsList items={resultContacts} />}
            />
        )
    }

    if (statKey === 'workExperienceCount') {
        const leftWe = leftCompany?.workExperience ?? []
        const rightWe = rightCompany?.workExperience ?? []
        const resultWe = mergeResult?.workExperience ?? []

        return (
            <ExpandedDetailRow
                left={<WorkExperienceList items={leftWe} />}
                right={<WorkExperienceList items={rightWe} />}
                result={<WorkExperienceList items={resultWe} />}
            />
        )
    }

    if (statKey === 'rostersCount') {
        const leftRosters = leftCompany?.rosters ?? []
        const rightRosters = rightCompany?.rosters ?? []
        const resultRosters = mergeResult?.rosters ?? []

        return (
            <ExpandedDetailRow
                left={<RostersList items={leftRosters} />}
                right={<RostersList items={rightRosters} />}
                result={<RostersList items={resultRosters} />}
            />
        )
    }

    if (statKey === 'jobBoardsCount') {
        const leftJb = leftCompany?.jobBoards ?? []
        const rightJb = rightCompany?.jobBoards ?? []
        const resultJb = mergeResult?.jobBoards ?? []

        return (
            <ExpandedDetailRow
                left={<JobBoardsList items={leftJb} />}
                right={<JobBoardsList items={rightJb} />}
                result={<JobBoardsList items={resultJb} />}
            />
        )
    }

    if (statKey === 'clientsCount') {
        const leftClients = leftCompany?.clients ?? []
        const rightClients = rightCompany?.clients ?? []
        const resultClients = mergeResult?.clients ?? []

        return (
            <ExpandedDetailRow
                left={<ClientsList items={leftClients} />}
                right={<ClientsList items={rightClients} />}
                result={<ClientsList items={resultClients} />}
            />
        )
    }

    if (statKey === 'candidateReferencesCount') {
        const leftRefs = leftCompany?.candidateReferences ?? []
        const rightRefs = rightCompany?.candidateReferences ?? []
        const resultRefs = mergeResult?.candidateReferences ?? []

        return (
            <ExpandedDetailRow
                left={<CandidateReferencesList items={leftRefs} />}
                right={<CandidateReferencesList items={rightRefs} />}
                result={<CandidateReferencesList items={resultRefs} />}
            />
        )
    }

    if (statKey === 'candidateNotesCount') {
        const leftNotes = [...(leftCompany?.candidateNotes ?? [])].sort(
            sortByDate
        )
        const rightNotes = [...(rightCompany?.candidateNotes ?? [])].sort(
            sortByDate
        )
        const resultNotes = [...(mergeResult?.candidateNotes ?? [])].sort(
            sortByDate
        )

        return (
            <ExpandedDetailRow
                left={<CandidateNotesList items={leftNotes} />}
                right={<CandidateNotesList items={rightNotes} />}
                result={<CandidateNotesList items={resultNotes} />}
            />
        )
    }

    if (statKey === 'groupNotesCount') {
        const leftNotes = [...(leftCompany?.groupNotes ?? [])].sort(sortByDate)
        const rightNotes = [...(rightCompany?.groupNotes ?? [])].sort(
            sortByDate
        )
        const resultNotes = [...(mergeResult?.groupNotes ?? [])].sort(
            sortByDate
        )

        return (
            <ExpandedDetailRow
                left={<GroupNotesList items={leftNotes} />}
                right={<GroupNotesList items={rightNotes} />}
                result={<GroupNotesList items={resultNotes} />}
            />
        )
    }

    return null
}

function ExpandedDetailRow({
    left,
    right,
    result,
}: {
    left: React.ReactNode
    right: React.ReactNode
    result: React.ReactNode
}) {
    return (
        <TableRow>
            <TableCell />
            <TableCell sx={{ verticalAlign: 'top', p: 1 }}>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>{left}</Box>
            </TableCell>
            <TableCell sx={{ verticalAlign: 'top', p: 1 }}>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>{right}</Box>
            </TableCell>
            <TableCell
                sx={{ verticalAlign: 'top', p: 1, bgcolor: 'action.hover' }}
            >
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>{result}</Box>
            </TableCell>
        </TableRow>
    )
}

function NotesList({
    notes,
}: {
    notes: readonly {
        id: string
        title: string
        noteData: string
        createdAt: string
        createdByUser?: { firstName: string; lastName: string } | null
    }[]
}) {
    if (notes.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {notes.map((note) => (
                <Box
                    key={note.id}
                    sx={{
                        borderLeft: 2,
                        borderColor: 'divider',
                        pl: 1,
                    }}
                >
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ display: 'block' }}
                    >
                        {note.title || '(untitled)'}
                    </Typography>
                    {note.noteData && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {note.noteData}
                        </Typography>
                    )}
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: 'block' }}
                    >
                        {note.createdByUser
                            ? `${note.createdByUser.firstName} ${note.createdByUser.lastName}`
                            : 'Unknown'}
                        {' · '}
                        {new Date(note.createdAt).toLocaleDateString()}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function fileNameFromKey(documentKey: string): string {
    const parts = documentKey.split('/')
    return decodeURIComponent(parts[parts.length - 1] || documentKey)
}

function DocumentsList({
    documents,
}: {
    documents: readonly { id: string; documentKey: string; createdAt: string }[]
}) {
    if (documents.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {documents.map((doc) => (
                <Box
                    key={doc.id}
                    sx={{
                        borderLeft: 2,
                        borderColor: 'divider',
                        pl: 1,
                    }}
                >
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ display: 'block' }}
                    >
                        {fileNameFromKey(doc.documentKey)}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: 'block' }}
                    >
                        {new Date(doc.createdAt).toLocaleDateString()}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function OfficesList({
    offices,
}: {
    offices: readonly {
        id: string
        isHeadquarters: boolean
        officeLocation?: { value: string } | null
    }[]
}) {
    if (offices.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {offices.map((office) => (
                <Box
                    key={office.id}
                    sx={{
                        borderLeft: 2,
                        borderColor: 'divider',
                        pl: 1,
                    }}
                >
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ display: 'block' }}
                    >
                        {office.officeLocation?.value ?? '(unknown)'}
                        {office.isHeadquarters && (
                            <Typography
                                component="span"
                                variant="caption"
                                color="primary.main"
                                sx={{ ml: 0.5 }}
                            >
                                (HQ)
                            </Typography>
                        )}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function IndustriesList({
    items,
}: {
    items: readonly {
        id: string
        industry?: { value: string } | null
        topTier: boolean
        isPrimary: boolean
    }[]
}) {
    if (items.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {items.map((item) => (
                <Box
                    key={item.id}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                >
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ display: 'block' }}
                    >
                        {item.industry?.value ?? '(unknown)'}
                        {item.topTier && (
                            <Typography
                                component="span"
                                variant="caption"
                                color="primary.main"
                                sx={{ ml: 0.5 }}
                            >
                                Top Tier
                            </Typography>
                        )}
                        {item.isPrimary && (
                            <Typography
                                component="span"
                                variant="caption"
                                color="success.main"
                                sx={{ ml: 0.5 }}
                            >
                                Primary
                            </Typography>
                        )}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function SectorsList({
    items,
}: {
    items: readonly {
        id: string
        sector?: { value: string } | null
        topTier: boolean
    }[]
}) {
    if (items.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {items.map((item) => (
                <Box
                    key={item.id}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                >
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ display: 'block' }}
                    >
                        {item.sector?.value ?? '(unknown)'}
                        {item.topTier && (
                            <Typography
                                component="span"
                                variant="caption"
                                color="primary.main"
                                sx={{ ml: 0.5 }}
                            >
                                Top Tier
                            </Typography>
                        )}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function FundsList({
    items,
}: {
    items: readonly {
        id: string
        name?: string | null
        fundSizeMillions: number
        year: number
    }[]
}) {
    if (items.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {items.map((item) => (
                <Box
                    key={item.id}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                >
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ display: 'block' }}
                    >
                        {item.name || '(unnamed)'} — ${item.fundSizeMillions}M
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: 'block' }}
                    >
                        {item.year}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function FundSizeBucketsList({
    items,
}: {
    items: readonly { id: string; value: string }[]
}) {
    if (items.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {items.map((item) => (
                <Box
                    key={item.id}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                >
                    <Typography variant="caption" fontWeight={600}>
                        {item.value}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function RecruiterContactsList({
    items,
}: {
    items: readonly {
        id: string
        group: string
        contactType: string
        recruiter?: {
            user?: { firstName: string; lastName: string } | null
        } | null
    }[]
}) {
    if (items.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {items.map((item) => (
                <Box
                    key={item.id}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                >
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ display: 'block' }}
                    >
                        {item.recruiter?.user
                            ? `${item.recruiter.user.firstName} ${item.recruiter.user.lastName}`
                            : '(unknown)'}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: 'block' }}
                    >
                        {item.contactType} · {item.group}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function WorkExperienceList({
    items,
}: {
    items: readonly {
        id: string
        startDate?: string | null
        endDate?: string | null
        isCurrent: boolean
        title?: { value: string } | null
    }[]
}) {
    if (items.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {items.map((item) => (
                <Box
                    key={item.id}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                >
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ display: 'block' }}
                    >
                        {item.title?.value ?? '(no title)'}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: 'block' }}
                    >
                        {item.startDate ?? '?'} –{' '}
                        {item.isCurrent ? 'Present' : item.endDate ?? '?'}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function GroupNotesList({
    items,
}: {
    items: readonly {
        id: string
        notePreview?: string | null
        createdAt: string
        createdByUser?: { firstName: string; lastName: string } | null
    }[]
}) {
    if (items.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {items.map((item) => (
                <Box
                    key={item.id}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                >
                    {item.notePreview && (
                        <Typography
                            variant="caption"
                            fontWeight={600}
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {item.notePreview}
                        </Typography>
                    )}
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: 'block' }}
                    >
                        {item.createdByUser
                            ? `${item.createdByUser.firstName} ${item.createdByUser.lastName}`
                            : 'Unknown'}
                        {' · '}
                        {new Date(item.createdAt).toLocaleDateString()}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function CandidateReferencesList({
    items,
}: {
    items: readonly {
        id: string
        firstName: string
        lastName: string
    }[]
}) {
    if (items.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {items.map((item) => (
                <Box
                    key={item.id}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                >
                    <Typography variant="caption" fontWeight={600}>
                        {item.firstName} {item.lastName}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function CandidateNotesList({
    items,
}: {
    items: readonly {
        id: string
        notePreview?: string | null
        createdAt: string
        createdByUser?: { firstName: string; lastName: string } | null
    }[]
}) {
    if (items.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {items.map((item) => (
                <Box
                    key={item.id}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                >
                    {item.notePreview && (
                        <Typography
                            variant="caption"
                            fontWeight={600}
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {item.notePreview}
                        </Typography>
                    )}
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: 'block' }}
                    >
                        {item.createdByUser
                            ? `${item.createdByUser.firstName} ${item.createdByUser.lastName}`
                            : 'Unknown'}
                        {' · '}
                        {new Date(item.createdAt).toLocaleDateString()}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function ClientsList({
    items,
}: {
    items: readonly {
        id: string
        isAdmin: boolean
        user?: { firstName: string; lastName: string } | null
    }[]
}) {
    if (items.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {items.map((item) => (
                <Box
                    key={item.id}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                >
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ display: 'block' }}
                    >
                        {item.user
                            ? `${item.user.firstName} ${item.user.lastName}`
                            : '(unknown)'}
                        {item.isAdmin && (
                            <Typography
                                component="span"
                                variant="caption"
                                color="primary.main"
                                sx={{ ml: 0.5 }}
                            >
                                Admin
                            </Typography>
                        )}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function JobBoardsList({
    items,
}: {
    items: readonly {
        id: string
        name: string
        description: string
        startDate: string
    }[]
}) {
    if (items.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {items.map((item) => (
                <Box
                    key={item.id}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                >
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ display: 'block' }}
                    >
                        {item.name}
                    </Typography>
                    {item.description && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {item.description}
                        </Typography>
                    )}
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: 'block' }}
                    >
                        {item.startDate}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function RostersList({
    items,
}: {
    items: readonly {
        id: string
        rosterName: string
        status: string
        rosterType: string
    }[]
}) {
    if (items.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {items.map((item) => (
                <Box
                    key={item.id}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                >
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ display: 'block' }}
                    >
                        {item.rosterName}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: 'block' }}
                    >
                        {item.status} · {item.rosterType}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}

function ResultCell({
    resultVal,
    winnerVal,
    previewing,
    hasPreview,
}: {
    resultVal?: number
    winnerVal: number
    previewing: boolean
    hasPreview: boolean
}) {
    if (previewing) {
        return (
            <Typography variant="body2" color="text.secondary">
                …
            </Typography>
        )
    }

    if (!hasPreview || resultVal === undefined) {
        return (
            <Typography variant="body2" color="text.secondary">
                —
            </Typography>
        )
    }

    const delta = resultVal - winnerVal

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>
                {resultVal}
            </Typography>
            {delta > 0 && (
                <Typography variant="caption" color="success.main">
                    +{delta}
                </Typography>
            )}
        </Box>
    )
}
